"use client";

import React, { useState } from "react";
import Link from "next/link";
import { CheckCircle2, User, FileText, Camera } from "lucide-react";

export default function MobileFeatures() {
	const [activeCard, setActiveCard] = useState<number | null>(null);

	const baseCardClasses =
		"backdrop-blur-md bg-gradient-to-br from-[rgba(101,222,158,0.06)] to-[rgba(22,37,28,0.45)] rounded-2xl p-6 flex flex-col gap-3 border text-left shadow-[0_12px_40px_-16px_rgba(0,0,0,0.5)] transition-all duration-300 transform";

	const getCardClasses = (index: number) => {
		const isActive = activeCard === index;
		const borderClass = isActive
			? "border-[#A7FF5E]/55 shadow-[0_0_0_1px_rgba(167,255,94,0.35)] ring-1 ring-[#A7FF5E]/25 scale-[1.02]"
			: "border-[rgba(255,255,255,0.12)] ring-1 ring-[#A7FF5E]/10 scale-100";
		return `${baseCardClasses} ${borderClass}`;
	};

	const PlatformBadge = ({
		eyebrow,
		tagline,
	}: {
		eyebrow: string;
		tagline: string;
	}) => (
		<div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
			<span className="text-[0.625rem] font-semibold uppercase tracking-wider text-[#A7FF5E]/80">
				{eyebrow}
			</span>
			<span className="text-white/25" aria-hidden>
				·
			</span>
			<span className="text-[0.625rem] font-medium uppercase tracking-wide text-white/45">
				{tagline}
			</span>
		</div>
	);

	const handleCardClick = (index: number) => {
		setActiveCard((current) => (current === index ? null : index));
	};

	return (
		<div className="flex flex-col gap-5 mb-8">
			<h2
				className="text-white text-2xl mb-8"
				style={{ marginBottom: "0rem" }}
			>
				Transform <span className="text-[#A7FF5E]">hours </span>
				of property research into
				<span className="text-[#A7FF5E]"> minutes</span> of actionable
				decisions.
			</h2>

			{/* Feature 1 - Verified Property Insights */}
			<button
				type="button"
				onClick={() => handleCardClick(0)}
				className={getCardClasses(0)}
			>
				<PlatformBadge eyebrow="Research" tagline="Included with Pro" />
				<div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl border border-white/12 bg-gradient-to-br from-[rgba(101,222,158,0.1)] to-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
					<CheckCircle2 className="w-7 h-7 text-[#25BC45]" />
				</div>
				<div className="flex-1">
					<h3 className="text-white font-black text-base mb-1.5 text-left">
						Verified Property Insights
					</h3>
					<p className="text-[rgba(255,255,255,0.72)] text-sm leading-relaxed text-left">
						Parcel, use, valuations, and risk signals in one
						place—shortlist liens before you dig deeper.
					</p>
					<p className="text-white/45 text-xs leading-snug mt-3 pt-3 border-t border-white/[0.08] text-left m-0">
						Search any address nationwide from your dashboard.
					</p>
				</div>
			</button>

			{/* Feature 2 - Accurate Owner Information */}
			<button
				type="button"
				onClick={() => handleCardClick(1)}
				className={getCardClasses(1)}
			>
				<PlatformBadge
					eyebrow="Contacts"
					tagline="Verified where available"
				/>
				<div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl border border-white/12 bg-gradient-to-br from-[rgba(101,222,158,0.1)] to-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
					<User className="w-7 h-7 text-[#25BC45]" />
				</div>
				<div className="flex-1">
					<h3 className="text-white font-black text-base mb-1.5 text-left">
						Accurate Owner Information
					</h3>
					<p className="text-[rgba(255,255,255,0.72)] text-sm leading-relaxed text-left">
						Match deeds to decision-makers—reach the right owner or
						entity without dead-end leads.
					</p>
					<p className="text-white/45 text-xs leading-snug mt-3 pt-3 border-t border-white/[0.08] text-left m-0">
						Tied to the same property record you’re reviewing.
					</p>
				</div>
			</button>

			{/* Feature 3 - Comprehensive Financial & Legal Data */}
			<button
				type="button"
				onClick={() => handleCardClick(2)}
				className={getCardClasses(2)}
			>
				<PlatformBadge
					eyebrow="Diligence"
					tagline="Liens, judgments & more"
				/>
				<div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl border border-white/12 bg-gradient-to-br from-[rgba(101,222,158,0.1)] to-transparent shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
					<FileText className="w-7 h-7 text-[#25BC45]" />
				</div>
				<div className="flex-1">
					<h3 className="text-white font-black text-base mb-1.5 text-left">
						Comprehensive Financial & Legal Data
					</h3>
					<p className="text-[rgba(255,255,255,0.72)] text-sm leading-relaxed text-left">
						Debt stacks, filings, and case references next to the
						asset—underwrite before you wire funds.
					</p>
					<p className="text-white/45 text-xs leading-snug mt-3 pt-3 border-t border-white/[0.08] text-left m-0">
						Everything references the same property you’re
						evaluating.
					</p>
				</div>
			</button>

			{/* Feature 4 — Property photos (hero / revenue) */}
			<Link
				href="/imagery"
				className="backdrop-blur-md rounded-2xl p-6 flex flex-col gap-4 border-2 border-[#A7FF5E]/55 bg-[rgba(167,255,94,0.09)] shadow-[0_0_36px_rgba(167,255,94,0.2),0_16px_48px_-16px_rgba(0,0,0,0.55)] ring-2 ring-[#A7FF5E]/35 transition-all duration-300 active:scale-[0.99] hover:border-[#A7FF5E] hover:shadow-[0_0_44px_rgba(167,255,94,0.28)] hover:scale-[1.02]"
				data-testid="mobile-feature-property-photos"
			>
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-[0.625rem] font-bold uppercase tracking-wider text-[#0f1b13] bg-[#A7FF5E] px-2 py-0.5 rounded-full">
						New
					</span>
					<span className="text-[0.625rem] font-semibold uppercase tracking-wide text-[#A7FF5E]">
						Order à la carte
					</span>
				</div>
				<div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-xl border-2 border-[#A7FF5E]/50 bg-[rgba(167,255,94,0.14)] shadow-[0_0_20px_rgba(167,255,94,0.25)]">
					<Camera
						className="w-7 h-7 text-[#A7FF5E]"
						strokeWidth={2}
					/>
				</div>
				<div className="flex-1 text-left">
					<h3 className="text-white font-black text-base mb-2">
						Ground-Level Property Photos
					</h3>
					<p className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed mb-3">
						Real exterior shots for any US address—perfect for
						remote diligence before you invest.
					</p>
					<ol className="list-none space-y-2 m-0 p-0 text-sm text-white/90">
						<li className="flex gap-2">
							<span className="font-bold text-[#A7FF5E] shrink-0">
								1.
							</span>
							Add the property address
						</li>
						<li className="flex gap-2">
							<span className="font-bold text-[#A7FF5E] shrink-0">
								2.
							</span>
							Pay securely online
						</li>
						<li className="flex gap-2">
							<span className="font-bold text-[#A7FF5E] shrink-0">
								3.
							</span>
							Photos reach you in 24–72 hours
						</li>
					</ol>
					<p className="text-[#A7FF5E] text-sm font-semibold mt-3 mb-0">
						Tap to order photos →
					</p>
				</div>
			</Link>
		</div>
	);
}
